import { SidebarInset, SidebarProvider, SidebarTrigger } from '@mgrvtechgroup/admin-ds';
import { Navigate, Outlet, useLocation } from 'react-router';
import { expiredSessionLoginPath } from '../auth/expired-session-route';
import { operatorSession, useOperatorSession } from '../auth/runtime';
import type { OperatorSession } from '../auth/session';
import { AppSidebar } from './app-sidebar';
import { APP_NAME, useDocumentTitle } from './document-title';
import { ShellHeaderSlotProvider, useShellHeaderSlot } from './shell-header';

/**
 * 셸 헤더 자리의 높이 규격 — 페이지마다 제목·설명·배지 유무로 헤더 높이가 달라 페이지를 오갈 때 본문이 위아래로 흔들리고
 * (로딩 중엔 설명이 비어 있다가 채워지며 또 밀린다), 헤더가 화면 위쪽을 크게 차지했다. 모든 페이지 헤더를 60px로 맞춘다.
 * 한 줄 제목(20/28) + 한 줄 설명(12/16, 넘치면 말줄임), 상세는 경로(12/16)와 제목(16/24)·설명(12/16) 세 줄을 촘촘하게 넣는다.
 * 높이는 고정(h)이 아니라 최소(min-h)다 — 고정하면 좁은 화면에서 액션이 아래로 내려갈 때 헤더 밖으로 넘쳐 아래 탭·본문을 가린다.
 * 페이지마다 고치지 않도록 admin-ds 헤더의 data-slot에 걸어 한곳에서 덮어쓴다.
 */
const SHELL_HEADER_CLASS = [
  '[&_[data-slot=page-header]]:min-h-15 [&_[data-slot=page-header]]:py-0',
  '[&_[data-slot=page-header]>div:first-child]:gap-0',
  '[&_[data-slot=page-header]_h1]:title-md',
  '[&_[data-slot=page-header]_p]:truncate [&_[data-slot=page-header]_p]:caption',
  '[&_[data-slot=detail-page-header]]:min-h-15 [&_[data-slot=detail-page-header]]:py-0',
  '[&_[data-slot=detail-page-header]>div:first-child>div:last-child]:gap-0',
  '[&_[data-slot=detail-page-header]_h1]:title-sm',
  '[&_[data-slot=detail-page-header]_p]:truncate [&_[data-slot=detail-page-header]_p]:caption',
  '[&_[data-slot=detail-page-header]_[data-slot=breadcrumb-list]]:caption',
].join(' ');

/**
 * 앱 골격 — admin-ds 공개 SidebarProvider/SidebarInset 조합으로 하나의 main을 소유한다.
 * 헤더 내용은 페이지가 <ShellHeader>로 채운다. 본문은 화면 높이에 고정하고 스크롤은 페이지 템플릿이 맡는다.
 */
export function RootLayout({ session = operatorSession }: { session?: OperatorSession }) {
  const { slot, slotRef } = useShellHeaderSlot();
  const location = useLocation();
  const { pathname } = location;
  const state = useOperatorSession(session);
  const isDemo = import.meta.env.DEV && (pathname === '/dev' || pathname.startsWith('/dev/'));
  const expiredLoginPath = expiredSessionLoginPath(
    pathname,
    location.search,
    state.status,
    state.generation,
  );
  const isEntering =
    state.status === 'authenticating' ||
    (state.status === 'unauthenticated' && state.generation === 0 && !state.reason);
  useDocumentTitle();

  if (expiredLoginPath) {
    return <Navigate replace to={expiredLoginPath} state={{ from: 'session-expiry' }} />;
  }

  // 로그인·콜백 화면은 RootLayout 밖에 있다 — 여기 오는 모든 화면이 인증 경계를 먼저 거친다.
  if (isEntering) return <Outlet />;

  return (
    <SidebarProvider className="h-svh min-h-0 overflow-hidden">
      <AppSidebar session={session} />
      <SidebarInset className="min-h-0 overflow-hidden">
        <div className="bg-background sticky top-0 z-10 shrink-0">
          {/* 768px 미만에서는 사이드바가 시트로 숨겨진다 — 여는 버튼을 사이드바 바깥 상단에 둔다. */}
          <div className="flex items-center gap-2 border-b px-[var(--layout-gutter)] py-2 md:hidden">
            <SidebarTrigger aria-label="메뉴 열기" title="메뉴 열기" />
            <span className="label">{APP_NAME}</span>
          </div>
          {isDemo && (
            <div role="note" className="caption border-b px-[var(--layout-gutter)] py-2">
              개발 전용 데모 · 합성 데이터이며 실제 지점이나 운영 결과가 아닙니다.
            </div>
          )}
          <div ref={slotRef} className={SHELL_HEADER_CLASS} />
        </div>
        <div className="min-h-0 flex-1 overflow-hidden">
          <ShellHeaderSlotProvider slot={slot}>
            <div className="flex h-full min-h-0 w-full min-w-0 flex-col">
              <Outlet />
            </div>
          </ShellHeaderSlotProvider>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
